import moment from "moment";
import { api } from "common";
import { Column } from "material-table";
import { useState, useRef } from "react";
import { styled } from '@mui/material/styles';
import { useTranslation } from "react-i18next";
import { IBannerType } from "common/src/types";
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSelector, useDispatch } from "react-redux";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CircularProgress from "@mui/material/CircularProgress";
import ImageColumn from "components/CustomTable/Column/ImageColumn";
import { CustomMaterialTable } from "components/CustomMaterialTable";
import { Modal, Typography, Button, Box, Paper, IconButton as MuiIconButton, Divider, Alert, Snackbar, Card, CardMedia, CardContent, CardActions, Backdrop, Fade } from "@mui/material";


const StyledModal = styled(Modal)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

const ModalContent = styled(Paper)(({ theme }) => ({
    width: '90%',
    maxWidth: 600,
    outline: 'none',
    position: 'relative',
    padding: theme.spacing(3),
    boxShadow: theme.shadows[24],
    borderRadius: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
}));

const DropZone = styled(Box)(({ theme }) => ({
    cursor: 'pointer',
    textAlign: 'center',
    padding: theme.spacing(4),
    transition: 'all 0.3s ease',
    borderRadius: theme.spacing(2),
    border: `2px dashed ${theme.palette.divider}`,
    '&:hover': { borderColor: theme.palette.primary.main, backgroundColor: theme.palette.action.hover },
}));

const PreviewImage = styled(CardMedia)({
    height: 200,
    backgroundSize: 'contain',
    backgroundPosition: 'center',
});

export default function Banners() {
    const { editBanners } = api;
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const banners = useSelector((state) => state.banners);
    const [profileModal, setOpenUploadModal] = useState(false);
    const [imageSelected, setImageSelected] = useState<File | null>(null);
    const [selectedRow, setSelectedRow] = useState<IBannerType | null>(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

    const settings = useSelector((state) => state.settingsdata.settings);

    if (!settings) return null;

    const UpdateImg = async () => {
        if (!imageSelected) {
            setSnackbar({
                open: true,
                message: t("select_image_first"),
                severity: 'error'
            });
            return;
        }

        setLoading(true);
        try {
            if (selectedRow) await dispatch(editBanners(selectedRow, "UpdateImage", imageSelected));
            setSnackbar({
                open: true,
                message: t("image_updated_successfully"),
                severity: 'success'
            });
            handleCloseModal();
        } catch (error) {
            setSnackbar({
                open: true,
                message: t("error_updating_image"),
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCloseModal = () => {
        setSelectedRow(null);
        setImageSelected(null);
        setOpenUploadModal(false);
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                setSnackbar({
                    open: true,
                    message: t("file_too_large_max_5mb"),
                    severity: 'error'
                });
                return;
            }
            if (!file.type.startsWith('image/')) {
                setSnackbar({
                    open: true,
                    message: t("only_image_files_allowed"),
                    severity: 'error'
                });
                return;
            }
            setImageSelected(file);
        }
    };

    const handleDropZoneClick = () => {
        fileInputRef.current?.click();
    };

    const handleRemoveImage = () => {
        setImageSelected(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const getImagePreview = () => {
        if (imageSelected) {
            return URL.createObjectURL(imageSelected);
        }
        return selectedRow?.photoUrl || null;
    };

    const columns: Column<IBannerType>[] = [
        {
            field: "createdAt",
            editable: "never",
            defaultSort: "desc",
            title: t("createdAt"),
            render: (rowData) => moment(rowData.createdAt).format('DD/MM/YYYY HH:mm'),
        },
        {
            field: "active",
            type: "boolean",
            title: t("active_status"),
        },
        {
            type: "boolean",
            field: "destaque",
            title: t("EMPHASIS")
        },
        {
            field: "modal",
            type: "boolean",
            title: t("MODAL"),
        },
        {
            type: "boolean",
            field: "forDriver",
            title: t("FOR_DRIVER"),
        },
        {
            type: "boolean",
            field: "forCustumer",
            title: t("FOR_CUSTOMER"),
        },
        {
            field: "name",
            type: "string",
            title: t("name"),
        },
        {
            field: "photoUrl",
            title: t("image"),
            render: (rowData) => (
                <ImageColumn
                    type="normal"
                    altText="photoUrl"
                    imageUrl={rowData.photoUrl}
                    onClick={() => { setSelectedRow(rowData); setOpenUploadModal(true); }}
                />
            )
        },
        {
            field: "link",
            type: "string",
            title: t("LINK"),
        },
    ];

    return (
        <>
            <CustomMaterialTable
                title={'banners'}
                columns={columns}
                data={banners.date ?? []}
                isLoading={banners.loading}
                editable={{
                    onRowAdd: (newData) =>
                        settings.AllowCriticalEditsAdmin
                            ? new Promise((resolve) => {
                                setTimeout(() => {
                                    newData["createdAt"] = new Date().getTime();
                                    dispatch(editBanners(newData, "Add"));
                                    setSelectedRow(null)
                                    resolve("");
                                }, 600);
                            })
                            : new Promise((resolve) => {
                                setTimeout(() => {
                                    resolve("");
                                    alert(t("demo_mode"));
                                }, 600);
                            }),
                    onRowUpdate: (newData) =>
                        settings.AllowCriticalEditsAdmin
                            ? new Promise((resolve) => {
                                setTimeout(() => {
                                    delete (newData as any).tableData;
                                    dispatch(editBanners(newData, "Update"));
                                    resolve("");
                                }, 600);
                            })
                            : new Promise((resolve) => {
                                setTimeout(() => {
                                    resolve("");
                                    alert(t("demo_mode"));
                                }, 600);
                            }),
                    onRowDelete: (oldData) =>
                        settings.AllowCriticalEditsAdmin
                            ? new Promise((resolve) => {
                                setTimeout(() => {
                                    dispatch(editBanners(oldData, "Delete"));
                                }, 600);
                                resolve("")
                            })
                            : new Promise((resolve) => {
                                setTimeout(() => {
                                    resolve("");
                                    alert(t("demo_mode"));
                                }, 600);
                            }),
                }}
            />

            <StyledModal
                open={profileModal}
                onClose={handleCloseModal}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={profileModal}>
                    <ModalContent elevation={3}>
                        {/* Header */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                                {t("update_banner_image")}
                            </Typography>
                            <MuiIconButton onClick={handleCloseModal} size="small">
                                <CloseIcon />
                            </MuiIconButton>
                        </Box>

                        <Divider sx={{ mb: 3 }} />

                        {selectedRow && (
                            <Card variant="outlined" sx={{ mb: 3, bgcolor: 'action.hover' }}>
                                <CardContent>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        {t("current_banner")}
                                    </Typography>
                                    <Typography variant="body1" fontWeight={500}>
                                        {selectedRow.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {t("id")}: {selectedRow.ref}
                                    </Typography>
                                </CardContent>
                            </Card>
                        )}

                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, mb: 2 }}>
                            {t("new_image")}
                        </Typography>

                        <input
                            type="file"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            onChange={handleFileSelect}
                            accept="image/jpeg, image/png, .jpg, .jpeg, .png"
                        />

                        {!getImagePreview() ? (
                            <DropZone onClick={handleDropZoneClick}>
                                <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                                <Typography variant="body1" color="text.secondary" gutterBottom>
                                    {t("click_or_drag_to_upload")}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {t("supported_formats_jpg_png_max_5mb")}
                                </Typography>
                            </DropZone>
                        ) : (
                            <Card variant="outlined" sx={{ mb: 3 }}>
                                <Box sx={{ position: 'relative' }}>
                                    <PreviewImage
                                        image={getImagePreview()!}
                                        title={t("image_preview")}
                                    />
                                    <IconButton
                                        sx={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            bgcolor: 'background.paper',
                                            '&:hover': { bgcolor: 'error.light', color: 'white' }
                                        }}
                                        size="small"
                                        onClick={handleRemoveImage}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                                <CardActions sx={{ justifyContent: 'space-between', px: 2, py: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        {imageSelected?.name}
                                    </Typography>
                                    <Button
                                        size="small"
                                        onClick={handleDropZoneClick}
                                        startIcon={<CloudUploadIcon />}
                                    >
                                        {t("change")}
                                    </Button>
                                </CardActions>
                            </Card>
                        )}

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                            <Button
                                variant="outlined"
                                disabled={loading}
                                onClick={handleCloseModal}
                            >
                                {t("cancel")}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={UpdateImg}
                                disabled={!imageSelected || loading}
                                startIcon={loading ? <CircularProgress size={20} /> : null}
                            >
                                {loading ? t("uploading") : t("save")}
                            </Button>
                        </Box>
                    </ModalContent>
                </Fade>
            </StyledModal>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    variant="filled"
                    sx={{ width: '100%' }}
                    severity={snackbar.severity}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}